
import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { featuredBars, barMatsAccessories } from '../../../mocks/equipmentHireCatalogue';

export default function HireInquiryForm() {
  const { ref, isVisible } = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    equipment: [] as string[],
    guestCount: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const allEquipment = [
    ...featuredBars.map((b) => b.name),
    ...barMatsAccessories.map((b) => b.name),
  ];

  const eventTypes = [
    'Wedding Reception',
    'Corporate Event',
    'Private Party',
    'Birthday Celebration',
    'Engagement Party',
    'Festival / Market',
    'Other',
  ];

  const toggleEquipment = (item: string) => {
    setFormData((prev) => ({
      ...prev,
      equipment: prev.equipment.includes(item)
        ? prev.equipment.filter((e) => e !== item)
        : [...prev.equipment, item],
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.eventDate) return;

    setStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('eventDate', formData.eventDate);
      body.append('eventType', formData.eventType);
      body.append('equipment', formData.equipment.join(', '));
      body.append('guestCount', formData.guestCount);
      body.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6e8lqatehdqnvnpja40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          eventType: '',
          equipment: [],
          guestCount: '',
          message: '',
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus('idle'), 5000);
  };

  return (
    <section id="hire-inquiry" className="py-20 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        <div
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 rounded-lg overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              {/* Left Side */}
              <div className="lg:w-2/5 p-8 lg:p-12 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-400/5 rounded-full blur-2xl"></div>

                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 mb-6">
                    <i className="ri-mail-send-line text-amber-300 text-xs"></i>
                    <span className="text-amber-200 text-[10px] tracking-widest uppercase font-medium">Enquire Now</span>
                  </div>

                  <h2 className="font-display text-3xl lg:text-4xl text-white mb-4 leading-tight">
                    Request Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-100">Equipment Quote</span>
                  </h2>
                  <p className="text-white/60 text-sm leading-relaxed mb-8">
                    Fill in your details and we&apos;ll send you a detailed quote within 24 hours. Our team will help you choose the perfect setup for your Melbourne event.
                  </p>

                  <div className="space-y-4">
                    {[
                      { icon: 'ri-time-line', title: 'Quick Response', desc: 'Quotes within 24 hours' },
                      { icon: 'ri-truck-line', title: 'Free Delivery', desc: 'Melbourne metro area' },
                      { icon: 'ri-customer-service-2-line', title: 'Expert Advice', desc: 'Personalised recommendations' },
                    ].map((item) => (
                      <div key={item.title} className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-amber-300 text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-white text-xs font-medium">{item.title}</p>
                          <p className="text-white/40 text-[11px]">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative z-10 mt-10 pt-6 border-t border-white/10">
                  <p className="text-white/40 text-xs mb-2">Or contact us directly</p>
                  <a href="mailto:hello@houseoffmr.com" className="text-amber-300 text-sm hover:text-amber-200 transition-colors cursor-pointer">
                    hello@houseoffmr.com
                  </a>
                  <br />
                  <a href="tel:+1234567890" className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer">
                    +1 (234) 567-890
                  </a>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="lg:w-3/5 bg-white p-8 lg:p-12 rounded-t-2xl lg:rounded-t-none lg:rounded-l-2xl">
                <form id="equipment-hire-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="Your full name"
                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        placeholder="you@email.com"
                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1.5">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        placeholder="+61 4XX XXX XXX"
                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1.5">Event Date *</label>
                      <input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1.5">Event Type</label>
                      <select
                        name="eventType"
                        value={formData.eventType}
                        onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all bg-white cursor-pointer"
                      >
                        <option value="">Select event type</option>
                        {eventTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1.5">Estimated Guests</label>
                      <input
                        type="number"
                        name="guestCount"
                        value={formData.guestCount}
                        onChange={(e) => setFormData({ ...formData, guestCount: e.target.value })}
                        placeholder="e.g. 100"
                        min="1"
                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-2">Equipment Interested In</label>
                    <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto p-3 bg-stone-50 rounded-lg border border-stone-100">
                      {allEquipment.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => toggleEquipment(item)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                            formData.equipment.includes(item)
                              ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600'
                          }`}
                        >
                          {formData.equipment.includes(item) && <i className="ri-check-line mr-1"></i>}
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1.5">Additional Details</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      maxLength={500}
                      rows={3}
                      placeholder="Tell us about your event, venue, or any special requirements..."
                      className="w-full px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all resize-none"
                    ></textarea>
                    <p className="text-right text-xs text-stone-400 mt-1">{formData.message.length}/500</p>
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 text-sm disabled:opacity-60"
                  >
                    {status === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>Sending...
                      </span>
                    ) : (
                      <span><i className="ri-send-plane-line mr-2"></i>Send Enquiry</span>
                    )}
                  </button>

                  {status === 'success' && (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <i className="ri-check-double-line text-emerald-600"></i>
                      <p className="text-emerald-700 text-xs font-medium">Your enquiry has been sent! We&apos;ll get back to you within 24 hours.</p>
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                      <i className="ri-error-warning-line text-rose-600"></i>
                      <p className="text-rose-700 text-xs font-medium">Something went wrong. Please try again or email us directly.</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
